import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "lib/utils";
import type { HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from "react";

const tableVariants = cva("table w-full", {
  variants: {
    density: {
      compact: "table-xs",
      normal: "table-sm",
      comfortable: "table-md",
    },
    zebra: {
      // DaisyUI の table-zebra は b1/b2 が近すぎて視認性が低いので自前で指定
      true: "[&_tbody_tr:nth-child(even)]:bg-slate-50",
    },
    bordered: {
      true: "[&_th]:border [&_td]:border [&_th]:border-slate-200 [&_td]:border-slate-200",
    },
  },
  defaultVariants: {
    density: "normal",
  },
});

type TableRootVariantProps = VariantProps<typeof tableVariants>;

type TableRootProps = {
  density?: TableRootVariantProps["density"];
  zebra?: boolean;
  bordered?: boolean;
  stickyHeader?: boolean;
} & Omit<HTMLAttributes<HTMLTableElement>, "className">;

/**
 * テーブルの最上位 primitive。DaisyUI の table をラップし、密度・ゼブラ・ボーダーのみ露出する。
 * className は意図的に受け取らない。スクロール領域は呼び出し側で制御する。
 * @param {TableRootProps} props Root の props
 * @return {JSX.Element} table 要素
 */
const TableRoot = ({
  density,
  zebra,
  bordered,
  stickyHeader,
  children,
  ...rest
}: TableRootProps): JSX.Element => {
  return (
    <div
      className={cn(
        "w-full overflow-x-auto",
        stickyHeader && "h-full overflow-y-auto",
      )}
    >
      <table
        className={cn(tableVariants({ density, zebra, bordered }))}
        {...rest}
      >
        {children}
      </table>
    </div>
  );
};

type TableHeadProps = {
  sticky?: boolean;
} & Omit<HTMLAttributes<HTMLTableSectionElement>, "className">;

/**
 * thead primitive。sticky=true で上端固定。
 * @param {TableHeadProps} props Head の props
 * @return {JSX.Element} thead 要素
 */
const TableHead = ({
  sticky,
  children,
  ...rest
}: TableHeadProps): JSX.Element => {
  return (
    <thead
      className={cn(sticky && "sticky top-0 z-10 bg-base-100 shadow-sm")}
      {...rest}
    >
      {children}
    </thead>
  );
};

type TableBodyProps = Omit<
  HTMLAttributes<HTMLTableSectionElement>,
  "className"
>;

/**
 * tbody primitive。
 * @param {TableBodyProps} props Body の props
 * @return {JSX.Element} tbody 要素
 */
const TableBody = ({ children, ...rest }: TableBodyProps): JSX.Element => {
  return <tbody {...rest}>{children}</tbody>;
};

type TableRowProps = {
  isClickable?: boolean;
  isSelected?: boolean;
  /** 行のトーン: default = 通常、muted = 既読などの低優先度行 */
  tone?: "default" | "muted";
} & Omit<HTMLAttributes<HTMLTableRowElement>, "className">;

/**
 * tr primitive。クリック可 / 選択状態 / トーンをスタイルで表現する。
 * @param {TableRowProps} props Row の props
 * @return {JSX.Element} tr 要素
 */
const TableRow = ({
  isClickable,
  isSelected,
  tone = "default",
  children,
  ...rest
}: TableRowProps): JSX.Element => {
  return (
    <tr
      className={cn(
        "border-b border-slate-200",
        tone === "muted" && "text-slate-400",
        isClickable && "cursor-pointer hover:bg-blue-50",
        isSelected && "bg-primary/10",
      )}
      {...rest}
    >
      {children}
    </tr>
  );
};

type TableCellProps = {
  align?: "left" | "center" | "right";
  nowrap?: boolean;
} & Omit<TdHTMLAttributes<HTMLTableCellElement>, "className">;

/**
 * td primitive。
 * @param {TableCellProps} props Cell の props
 * @return {JSX.Element} td 要素
 */
const TableCell = ({
  align = "left",
  nowrap,
  children,
  ...rest
}: TableCellProps): JSX.Element => {
  return (
    <td
      className={cn(
        "px-2 py-0.5 align-middle text-xs leading-tight",
        align === "center" && "text-center",
        align === "right" && "text-right",
        nowrap && "whitespace-nowrap",
      )}
      {...rest}
    >
      {children}
    </td>
  );
};

type TableHeaderCellProps = {
  align?: "left" | "center" | "right";
  nowrap?: boolean;
  isSortable?: boolean;
  sortDirection?: "asc" | "desc" | false;
} & Omit<ThHTMLAttributes<HTMLTableCellElement>, "className">;

/**
 * th primitive。ソート可能な列のインジケータ（▲/▼）を含む。
 * @param {TableHeaderCellProps} props HeaderCell の props
 * @return {JSX.Element} th 要素
 */
const TableHeaderCell = ({
  align = "left",
  nowrap = true,
  isSortable,
  sortDirection,
  children,
  ...rest
}: TableHeaderCellProps): JSX.Element => {
  const indicator =
    sortDirection === "asc" ? "▲" : sortDirection === "desc" ? "▼" : "";
  return (
    <th
      className={cn(
        "px-2 py-1 font-semibold text-xs leading-tight tracking-wide !text-white bg-primary border-b border-primary",
        align === "center" && "text-center",
        align === "right" && "text-right",
        nowrap && "whitespace-nowrap",
        isSortable && "cursor-pointer select-none hover:bg-primary/90",
      )}
      {...rest}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {indicator && (
          <span className="text-[10px] text-white/80">{indicator}</span>
        )}
      </span>
    </th>
  );
};

export const Table = {
  Root: TableRoot,
  Head: TableHead,
  Body: TableBody,
  Row: TableRow,
  Cell: TableCell,
  HeaderCell: TableHeaderCell,
};

export type {
  TableRootProps,
  TableHeadProps,
  TableBodyProps,
  TableRowProps,
  TableCellProps,
  TableHeaderCellProps,
};
